import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "@/lib/motion";
import {
    ArrowUpRight,
    ServerCog,
    ShieldCheck,
    Cloud,
    Compass,
    Workflow,
    LifeBuoy,
    } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Overline } from "@/components/Reveal";
import { MountReveal, CtaLink } from "@/components/pageKit";
import { useSeo } from "@/lib/useSeo";
import { getServices, getNavigation, getServicesPage } from "@/lib/sanityQueries";
import { peek } from "@/lib/sanity";

const ease = [0.22, 1, 0.36, 1];

// Frontend-owned service icons mapped by stable slug (falls back by position).
const ICONS_BY_SLUG = {
    "managed-it-operational-excellence": ServerCog,
    "cybersecurity-risk-protection": ShieldCheck,
    "cloud-infrastructure-scalability": Cloud,
    "it-strategy-advisory-vcio": Compass,
    "digital-experience-custom-solutions": Workflow,
    "business-continuity-resilience": LifeBuoy,
};
const FALLBACK_ICONS = [ServerCog, ShieldCheck, Cloud, Compass, Workflow, LifeBuoy];

// No dedicated Sanity page-level SEO exists for the Services overview, so use a
// conservative frontend fallback derived from EasiLinked's service positioning.
const FALLBACK_SEO = {
    metaTitle: "IT & Cybersecurity Services | EasiLinked",
    metaDescription:
        "Managed IT, cybersecurity, cloud & infrastructure, IT strategy & vCIO, digital solutions, and business continuity services for growing organizations.",
    canonicalUrl: "https://easilinked.com/services",
    noIndex: false,
};

const ServicesContent = () => {
    const [services, setServices] = useState(() => {
        const snap = peek(getServices);
        return snap?.length ? snap : [];
    });
    const [navCta, setNavCta] = useState(() => peek(getNavigation)?.primaryCta ?? null);
    const [pageData, setPageData] = useState(() => peek(getServicesPage));
    useSeo(pageData?.seo || FALLBACK_SEO);

    useEffect(() => {
        let active = true;
        getServices()
            .then((res) => {
                if (active && res?.length) setServices(res);
            })
            .catch(() => {});
        getNavigation()
            .then((res) => {
                if (active && res?.primaryCta) setNavCta(res.primaryCta);
            })
            .catch(() => {});
        getServicesPage()
            .then((res) => {
                if (active && res) setPageData(res);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    const bookingUrl = navCta?.url || "#";
    const bookingLabel = navCta?.label || "Schedule a Conversation";

    return (
        <>
            {/* HERO */}
            <section
                data-testid="services-hero"
                className="relative overflow-hidden pt-36 pb-16 sm:pt-44 sm:pb-20"
            >
                <div className="pointer-events-none absolute inset-0 el-grid-bg" aria-hidden />
                <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
                    <MountReveal>
                        <span className="inline-flex items-center">
                            <Overline>{pageData?.heroEyebrow || "Capabilities"}</Overline>
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h1
                            data-testid="services-hero-heading"
                            className="mt-7 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-6xl"
                        >
                            {pageData?.heroHeadline ||
                                "Technology Services Built Around Business Outcomes."}
                        </h1>
                    </MountReveal>
                    <MountReveal delay={0.1}>
                        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-brand-muted sm:text-lg">
                            {pageData?.heroSupportingMessage ||
                                "Managed operations, cybersecurity, cloud infrastructure, and strategic guidance — delivered as a single, business-first technology partnership for growing organizations."}
                        </p>
                    </MountReveal>
                </div>
            </section>

            {/* SERVICES GRID */}
            <section
                data-testid="services-grid"
                className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 sm:pb-28"
            >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {services.map((s, i) => {
                        const Icon = ICONS_BY_SLUG[s.slug] || FALLBACK_ICONS[i % FALLBACK_ICONS.length];
                        return (
                            <MountReveal key={s._id || s.slug || i} delay={(i % 3) * 0.06}>
                                <Link
                                    to={`/services/${s.slug}`}
                                    data-testid={`service-card-${s.slug}`}
                                    className="group flex h-full flex-col rounded-3xl border border-brand-line bg-brand-surface p-7 transition-colors duration-150 hover:border-brand-accent/40"
                                >
                                    <div className="flex items-start justify-between">
                                        <span className="grid h-14 w-14 place-items-center rounded-2xl border border-brand-line bg-brand-bg text-brand-accent transition-colors duration-150 group-hover:bg-brand-accent group-hover:text-brand-on-accent">
                                            <Icon className="h-6 w-6" />
                                        </span>
                                        <ArrowUpRight className="h-5 w-5 text-brand-muted transition-colors duration-150 group-hover:text-brand-accent" />
                                    </div>
                                    <h2 className="mt-6 font-heading text-xl font-bold tracking-tight text-brand-text">
                                        {s.name}
                                    </h2>
                                    <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                                        {s.shortDescription}
                                    </p>
                                    <span className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-accent">
                                        View service
                                        <ArrowUpRight className="h-3.5 w-3.5" />
                                    </span>
                                </Link>
                            </MountReveal>
                        );
                    })}
                </div>
            </section>

            {/* CLOSING CTA */}
            <section
                data-testid="services-closing-cta"
                className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 sm:pb-28"
            >
                <div className="relative overflow-hidden rounded-card border border-brand-line bg-brand-surface px-6 py-20 text-center sm:px-16 sm:py-24">
                    <div className="pointer-events-none absolute inset-0 el-grid-bg opacity-40" aria-hidden />
                    <MountReveal>
                        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-accent">
                            {pageData?.closingEyebrow || "Not sure where to start?"}
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h2 className="mx-auto mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.0] tracking-tight text-brand-text sm:text-5xl">
                            {pageData?.closingHeadline ||
                                "Let's map the right services to your business priorities."}
                        </h2>
                    </MountReveal>
                    <MountReveal delay={0.1}>
                        <div className="mt-10 flex justify-center">
                            <CtaLink
                                href={bookingUrl}
                                label={bookingLabel}
                                openInNewTab
                                variant="primary"
                                testid="services-closing-cta-button"
                            />
                        </div>
                    </MountReveal>
                </div>
            </section>
        </>
    );
};

export default function ServicesPage() {
    return (
        <PageLayout>
            <ServicesContent />
        </PageLayout>
    );
}
